import React, { PropsWithChildren } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { BodyBold } from '@components/dashboard/Typography';
import Colors from '@components/shared/Colors';
import TextLinkButton from '@components/shared/TextLinkButton';
import { PageOptions } from '@contracts';
import IconArrow from '@images/vms-account/vms-icons/Arrow.svg';
import ExclamationYellow from '@images/vms-account/vms-icons/ExclamationYellow.svg';
import TickGreen from '@images/vms-account/vms-icons/TickGreen.svg';
import useNavigate from '@utils/useNavigate';

interface Props {
  textColor?: string;
  banner?: {
    type: string;
    text: string;
  };
  progress?: number;
  padding?: string;
  style?: React.CSSProperties | undefined;
  hideBack?: boolean;
  width?: string;
}

const PageContainerTemplate: React.FC<PropsWithChildren<Props>> = ({
  children,
  banner,
  progress,
  textColor = Colors.navyBlue,
  padding = '50px 60px',
  style,
  hideBack,
  width = '683px'
}) => {
  const { navigateWrapper } = useNavigate();
  const { pathname } = useLocation();
  const pathPages = pathname.split('/').filter((e) => e);
  const hasBackButton = [
    '/health-profile/',
    '/screening-report/',
    '/how-to-use/',
    '/meet-microbiome/',
    '/founders-microbiome/',
    '/subscriptions',
    '/what-is-vaginal-microbiome-screening',
    '/arrange-sample-collection'
  ];
  const previousPath = pathPages[pathPages.length - 2];

  const getBackButtonText = () => {
    if (hasBackButton.filter((path) => pathname.includes(path)).length < 1) return '';
    switch (previousPath) {
      case PageOptions.Dashboard:
        return 'Dashboard';
      case PageOptions.MicrobiomeScreening:
        return 'Microbiome Screening';
      default:
        return '';
    }
  };

  const getPreviousPageURL = () => {
    switch (previousPath) {
      case PageOptions.MicrobiomeScreening:
        return `/dashboard/${PageOptions.MicrobiomeScreening}/`;
      case PageOptions.Purchases:
        return `/dashboard/${PageOptions.Purchases}/`;
      case PageOptions.Dashboard:
      default:
        return `/dashboard/`;
    }
  };

  const backButtonText = getBackButtonText();
  const previousPageURL = getPreviousPageURL();

  const renderBanner = () => {
    if (!banner) return null;
    if (banner.type === 'success') {
      return (
        <Banner>
          <TickGreen />
          <BodyBold>{banner.text}</BodyBold>
        </Banner>
      );
    }
    return (
      <Banner $background={`${Colors.ecru50}`}>
        <ExclamationYellow />
        <BodyBold>{banner.text}</BodyBold>
      </Banner>
    );
  };

  return (
    <Container $width={width} className="page-container-template">
      {banner ? renderBanner() : null}
      {progress ? <ProgressIndicator $percentage={progress} /> : null}
      <InnerContainer $textColor={textColor} $padding={padding} style={style}>
        {!hideBack && pathPages.length > 1 && backButtonText !== '' ? (
          <TextLinkButtonStyled onClick={() => navigateWrapper(previousPageURL)}>
            <IconArrow style={{ transform: 'rotate(90deg)' }} color={Colors.navyBlue} />
            {getBackButtonText()}
          </TextLinkButtonStyled>
        ) : null}
        {children}
      </InnerContainer>
    </Container>
  );
};

export default PageContainerTemplate;

const Container = styled.div<{ $width: string }>`
  background: ${Colors.cream};
  width: ${(props) => props.$width};
  margin-bottom: 100px;
  box-shadow: 0 2px 2px rgba(0, 29, 59, 0.1);
  border-radius: 3px;
  max-width: 100%;

  @media (max-width: 1300px) {
    ${(props) => (props.$width === '900px' ? 'width: 760px;' : '')};
  }

  @media (max-width: 768px) {
    margin-bottom: 40px;
    border-radius: 0px;
  }
`;

const Banner = styled.div<{ $background?: string }>`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  padding: 20px 30px;
  gap: 20px;
  background: ${(props) => props.$background ?? `${Colors.mint50}`};
  border-radius: 3px 3px 2px 2px;
`;

interface StyleProps {
  $textColor?: string;
  $padding?: string;
}

interface ProgressProps {
  $percentage: number;
}

const ProgressIndicator = styled.div<ProgressProps>`
  width: 100%;
  height: 3px;
  background: ${Colors.paleGold};
  border-radius: 0;
  position: sticky;
  top: 0;
  z-index: 2;

  &:before {
    content: '';
    display: block;
    background: ${Colors.gold};
    height: 100%;
    width: ${(props) => `${props.$percentage}%`};
  }
`;

const InnerContainer = styled.div<StyleProps>`
  color: ${(props) => props.$textColor};
  padding: ${(props) => props.$padding};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;

  & > div {
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: ${(props) => (props.$padding !== '0' ? '30px 24px' : 0)};
    margin-bottom: 0;
  }
`;

const TextLinkButtonStyled = styled(TextLinkButton)`
  margin-bottom: 15px;
  border-bottom: none;
  font-weight: 400;
  color: ${Colors.navyBlue};
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0;
  gap: 10px;
`;
